/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { IoMdArrowBack, IoMdArrowForward } from 'react-icons/io';
import { toast } from 'react-toastify';
import { Field, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import ContainedButton from 'components/layout/form/containedButton';
import OutlinedButton from 'components/layout/form/outlinedButton';
import TextField from 'components/layout/form/textField';

import { useMFAContext } from 'hooks/useMFA';

import {
  removeAllItemsLocalStorage,
  requestGetAccessToken,
  requestGetUserInfos,
  requestOauth,
} from 'services/authServices';
import ResetPassword from 'newComponents/ResetPassword';
import { getSigninInfo } from 'store/actions/signin-actions';
import { handleUserId } from 'store/actions/user-actions';
import { useAuth } from 'hooks/authHook';
import { sendToken } from '../services';

import { HeaderContainer } from '../styles';

import {
  Container,
  ContainerButtons,
  FormContainer,
  TextTimer,
} from './styles';

const formatTimer = (timer) => {
  let minutes = parseInt(timer / 60, 10);
  let seconds = parseInt(timer % 60, 10);
  minutes = minutes < 10 ? `0${minutes}` : minutes;
  seconds = seconds < 10 ? `0${seconds}` : seconds;
  return `${minutes}:${seconds}`;
};

const ValidationToken = ({ setStep, userInfo, contactId = null }) => {
  const [loading, setLoading] = useState();
  const [timer, setTimer] = useState(60);
  const [startTimer, setStartTimer] = useState(false);
  const [showModalResetPass, setShowModalResetPass] = useState(false);

  const { credentials, setCredentials, setAttempts, attempts } =
    useMFAContext();

  const { signIn, setUserInfos } = useAuth();

  const dispatch = useDispatch();
  const history = useHistory();

  const intervalRef = useRef();
  let newTimer = 60;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const decreaseNum = () => {
    if (newTimer === 0) {
      setStartTimer(false);
      clearInterval(intervalRef.current);
      return;
    }
    setTimer((prev) => {
      newTimer -= 1;
      return prev - 1;
    });
  };

  useEffect(() => {
    if (startTimer) {
      intervalRef.current = setInterval(decreaseNum, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [startTimer, decreaseNum]);

  useEffect(() => {
    setStartTimer(true);
  }, []);

  const contact = userInfo.contacts?.find(
    (ct) => ct.contactId === userInfo?.validationContactId
  );

  const handleSelectAddress = (
    customerSiteCode,
    customerSiteId,
    customerCode,
    customerId,
    customerName,
    addressLine,
    addressNumber,
    userCustomerRelationshipId,
    document
  ) => {
    setLoading(false);
    setUserInfos({
      customerSiteCode,
      customerSiteId,
      customerCode,
      customerId,
      customerName,
      customerAddressLine: `${addressLine}, ${addressNumber}`,
      userCustomerRelationshipId,
      document,
    });
  };

  const resendToken = async () => {
    setLoading(true);
    setAttempts(attempts + 1);

    if (attempts + 1 >= 3) {
      return setStep(1);
    }

    const success = await sendToken(contact, userInfo?.userId, contactId);

    if (success) {
      setTimer(60);
      setStartTimer(true);
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    if (!loading) {
      try {
        setLoading(true);

        const { token } = values;

        setCredentials({ ...credentials, token });

        removeAllItemsLocalStorage();

        const accessToken = await requestGetAccessToken();

        const { login, password } = credentials;

        const responseOauth = await requestOauth(
          login,
          password,
          token,
          accessToken,
          dispatch,
          getSigninInfo
        );

        if (responseOauth) {
          signIn({
            loginEmail: login,
            accessToken: responseOauth.access_token,
            refreshToken: responseOauth.refresh_token,
            customerType: 'Granel',
          });
          requestGetUserInfos(
            login,
            responseOauth.access_token,
            dispatch,
            handleUserId,
            setUserInfos,
            handleSelectAddress,
            history
          );

          return true;
        }
      } catch (error) {
        setLoading(false);
        const erro = error?.response;

        const code = error?.response?.data?.code;
        const message = error?.response?.data?.message;
        const status = erro?.status;

        if (status === 401) {
          if (code === '401.004') {
            setShowModalResetPass(true);
          } else if (code === '401.006') {
            toast.warn(message);
          } else if (code === '401.005') {
            toast.error(message);
          } else {
            toast.error(message);
          }
        }

        if (status === 400) {
          toast.error('Autenticação inválida');
          return null;
        }
        if (erro) {
          if (status !== 401) {
            toast.error(message);
          }
        } else if (status !== 401) {
          toast.error('Ocorreu um erro interno na aplicação');
        }
      }

      return null;
    }
  };

  const validatorSchema = () =>
    Yup.object().shape({
      token: Yup.string().required('É necessário inserir o token'),
    });

  return (
    <Container>
      <HeaderContainer>
        <h1>Insira o código para continuar com o login</h1>
        <h2>Informe o código recebido</h2>
      </HeaderContainer>

      <Formik
        initialValues={{
          token: '',
        }}
        validationSchema={validatorSchema}
        onSubmit={handleSubmit}
        render={({ handleChange, handleBlur, values, errors, touched }) => (
          <FormContainer>
            <Field
              name="token"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Inserir código"
                  placeholder="Inserir código"
                  errorCondition={errors.token && touched.token}
                  errorMessage={errors.token}
                  isCorrect={!errors.token}
                  value={values.token}
                  onBlur={handleBlur}
                  onClick={() => toast.dismiss()}
                  onChange={handleChange}
                />
              )}
            />
            <TextTimer>
              Não recebi o código.
              {timer > 0 ? (
                `Aguarde ${formatTimer(timer)}`
              ) : (
                // eslint-disable-next-line jsx-a11y/anchor-is-valid
                <a href="#" onClick={resendToken}>
                  Enviar novamente
                </a>
              )}
            </TextTimer>
            <ContainerButtons>
              <ContainedButton
                block
                type="submit"
                continue
                loading={loading}
                disabled={loading || !values.token}
                id="Avançar"
                width="100%"
                TrailingIcon={() => <IoMdArrowForward size={20} />}
              >
                Validar código
              </ContainedButton>
              <OutlinedButton
                width="100%"
                LeadingIcon={() => <IoMdArrowBack size={20} />}
                onClick={() => setStep(1)}
              >
                Voltar
              </OutlinedButton>
            </ContainerButtons>
          </FormContainer>
        )}
      />
      <ResetPassword
        setShowModal={setShowModalResetPass}
        showModal={showModalResetPass}
      />
    </Container>
  );
};

export default ValidationToken;
